/**
 * BreadcrumbNav — clickable breadcrumb trail from path_cache.
 */

import { ChevronRight, FolderOpen } from 'lucide-react'
import type { FolderNode } from '../../types/fileManager'

interface BreadcrumbNavProps {
  pathCache: string | null
  folderTree: FolderNode[]
  onNavigate: (folderId: number | null) => void
}

/** Walk the tree to find a folder by name at each depth level. */
function resolvePathSegments(
  tree: FolderNode[],
  segments: string[],
): { name: string; id: number | null }[] {
  const result: { name: string; id: number | null }[] = []
  let current: FolderNode[] = tree

  for (const seg of segments) {
    const match = current.find(f => f.name === seg)
    if (match) {
      result.push({ name: match.name, id: match.id })
      current = match.children ?? []
    } else {
      result.push({ name: seg, id: null })
      break
    }
  }
  return result
}

export default function BreadcrumbNav({ pathCache, folderTree, onNavigate }: BreadcrumbNavProps) {
  if (!pathCache) {
    return (
      <div className="flex items-center gap-1.5 text-sm text-[var(--rq-text-soft)]">
        <FolderOpen className="w-4 h-4" />
        <button onClick={() => onNavigate(null)} className="hover:text-[var(--rq-text)] transition-colors">
          All Assets
        </button>
      </div>
    )
  }

  const segments = pathCache.split('/')
  // Skip first segment ("Assets") since it's the root name
  const treeSegments = segments.length > 1 ? segments.slice(1) : segments

  // Find root folder
  const root = folderTree.length > 0 ? folderTree[0] : null
  const rootChildren = root?.children ?? []
  const resolved = resolvePathSegments(rootChildren, treeSegments)

  return (
    <div className="flex items-center gap-1 text-sm flex-wrap">
      <FolderOpen className="w-4 h-4 text-[var(--rq-text-soft)] flex-shrink-0" />
      <button
        onClick={() => onNavigate(null)}
        className="text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] transition-colors"
      >
        All Assets
      </button>
      {resolved.map((seg, i) => (
        <span key={i} className="flex items-center gap-1">
          <ChevronRight className="w-3.5 h-3.5 text-[var(--rq-text-soft)] flex-shrink-0" />
          {i === resolved.length - 1 ? (
            <span className="text-[var(--rq-text)] font-medium">{seg.name}</span>
          ) : (
            <button
              onClick={() => seg.id !== null && onNavigate(seg.id)}
              className="text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] transition-colors"
            >
              {seg.name}
            </button>
          )}
        </span>
      ))}
    </div>
  )
}
